/**
 * Market switch:
 * - Affiche la suggestion géographique.
 * - Mémorise la boutique et la langue choisies pendant 30 jours.
 * - Respecte les changements de langue dans le marché actuel.
 * - Rétablit le marché mémorisé sur les accueils généraux.
 */
if (!customElements.get('market-switch')) {
  class MarketSwitch extends HTMLElement {
    constructor() {
      super();

      const configEl = document.getElementById('MarketSwitchConfig');
      if (!configEl) return;

      this.config = JSON.parse(configEl.textContent);

      this.geoDialog = this.querySelector('[data-geo-dialog]');
      this.manualDialog = this.querySelector('[data-manual-dialog]');

      this.wireDialog(this.geoDialog, 'geo');
      this.wireDialog(this.manualDialog, 'manual');
      this.bindManualText();

      document.querySelectorAll('[data-market-switch-open]').forEach((btn) => {
        btn.addEventListener('click', () => this.openManual());
      });

      this.initDropdown();
      this.checkGeo();
    }

    initDropdown() {
      const toggle = document.querySelector('[data-market-switch-toggle]');
      const menu = document.querySelector('[data-market-switch-menu]');

      if (!toggle || !menu) return;

      const closeMenu = () => {
        menu.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
      };

      toggle.addEventListener('click', (event) => {
        event.stopPropagation();

        const isOpen = !menu.hidden;
        menu.hidden = isOpen;
        toggle.setAttribute('aria-expanded', String(!isOpen));
      });

      menu.querySelectorAll('[data-market-switch-open]').forEach((option) => {
        option.addEventListener('click', closeMenu);
      });

      document.addEventListener('click', (event) => {
        if (
          !menu.hidden &&
          !menu.contains(event.target) &&
          !toggle.contains(event.target)
        ) {
          closeMenu();
        }
      });

      document.addEventListener('keyup', (event) => {
        if (event.key === 'Escape') closeMenu();
      });
    }

    wireDialog(dialog, key) {
      if (!dialog) return;

      dialog.querySelector('[data-close]').addEventListener('click', () => {
        this.close(dialog, key);
      });

      dialog.querySelector('[data-secondary]').addEventListener('click', () => {
        this.close(dialog, key);
      });

      dialog.querySelector('[data-primary]').addEventListener('click', () => {
        const localeKey = key === 'geo'
          ? this.geoTargetInfo().key
          : this.config.equivalents[this.config.localeKey];

        this.setDismissCookie(localeKey);
        window.location.href = this.config.targets[localeKey] || '/';
      });
    }

    bindManualText() {
      if (!this.manualDialog) return;

      const localeStrings = this.config.i18n[this.config.localeKey];
      const strings = localeStrings && localeStrings.manual;

      this.fillDialog(this.manualDialog, strings);
    }

    fillDialog(dialog, strings) {
      if (!dialog || !strings) return;

      dialog.querySelector('[data-message]').textContent = strings.message;
      dialog.querySelector('[data-primary]').textContent = strings.primary;
      dialog.querySelector('[data-secondary]').textContent = strings.secondary;
      dialog.querySelector('[data-close]').setAttribute(
        'aria-label',
        strings.close
      );
    }

    targetMarket() {
      return this.config.currentMarket === 'eu' ? 'can' : 'eu';
    }

    equivalentTargetUrl() {
      const equivalent = this.config.equivalents[this.config.localeKey];
      return this.config.targets[equivalent] || '/';
    }

    geoTargetInfo() {
      const market = this.targetMarket();

      if (market === 'eu') {
        const key = (
          this.detectedCountry &&
          this.config.countryLangMap[this.detectedCountry]
        ) || 'en-eu';

        return { direction: 'toEU', key };
      }

      const browserLang = (
        (navigator.language || navigator.userLanguage || '').split('-')[0] || ''
      ).toLowerCase();

      const key = browserLang === 'en' ? 'en' : 'fr';

      return { direction: 'toCAN', key };
    }

    geoTargetUrl() {
      return this.config.targets[this.geoTargetInfo().key] || '/';
    }

    showGeoDialog() {
      if (!this.geoDialog) return;

      const { direction, key } = this.geoTargetInfo();
      const messages = this.config.geoMessages[direction];
      const strings = messages && messages[key];

      this.fillDialog(this.geoDialog, strings);

      if (strings) this.open(this.geoDialog);
    }

    open(dialog) {
      if (!dialog) return;
      dialog.hidden = false;
    }

    close(dialog, key) {
      if (!dialog) return;

      dialog.hidden = true;

      if (key === 'geo') {
        this.setDismissCookie();
      }
    }

    openManual() {
      if (!this.config.showManualPopup) {
        const localeKey = this.config.equivalents[this.config.localeKey];

        this.setDismissCookie(localeKey);
        window.location.href = this.config.targets[localeKey] || '/';
        return;
      }

      this.open(this.manualDialog);
    }

    checkGeo() {
      const debug = new URLSearchParams(window.location.search).get('ms_debug');

      if (debug === 'eu' || debug === 'can') {
        if (debug !== this.config.currentMarket) {
          this.showGeoDialog();
        }

        return;
      }

      const preference = this.getCookie(this.config.cookieName);

      const savedTarget = Object.prototype.hasOwnProperty.call(
        this.config.targets,
        preference
      ) ? this.config.targets[preference] : null;

      if (savedTarget) {
        const savedMarket = preference.endsWith('-eu') ? 'eu' : 'can';

        // Dans le même marché, respecter la langue de la page visitée
        // et mettre à jour la préférence mémorisée.
        if (savedMarket === this.config.currentMarket) {
          if (preference !== this.config.localeKey) {
            this.setDismissCookie(this.config.localeKey);
          }

          return;
        }

        // Si le marché mémorisé est différent, conserver le retour
        // vers ce marché sur les accueils généraux uniquement.
        // Les liens Europe explicites et les pages internes
        // restent accessibles.
        const path = window.location.pathname.replace(/\/+$/, '') || '/';

        if (path === '/' || path === '/en') {
          const targetPath = savedTarget.replace(/\/+$/, '') || '/';

          if (path !== targetPath) {
            window.location.replace(
              savedTarget + window.location.search + window.location.hash
            );
          }
        }

        return;
      }

      if (!this.config.showGeoPopup || !this.geoDialog) return;

      fetch('/browsing_context_suggestions.json')
        .then((res) => {
          if (!res.ok) {
            throw new Error('Country detection failed');
          }

          return res.json();
        })
        .then((data) => {
          const detected = data && data.detected_values;
          const countryCode = (
            detected &&
            detected.country &&
            detected.country.handle
          ) || null;

          if (!countryCode) return;

          this.detectedCountry = countryCode.toUpperCase();

          const detectedMarket = this.config.euCountries.includes(
            this.detectedCountry
          ) ? 'eu' : 'can';

          if (detectedMarket !== this.config.currentMarket) {
            this.showGeoDialog();
          }
        })
        .catch(() => {});
    }

    setDismissCookie(localeKey = this.config.localeKey) {
      if (
        !Object.prototype.hasOwnProperty.call(this.config.targets, localeKey)
      ) {
        return;
      }

      const maxAge = this.config.cookieDays * 24 * 60 * 60;
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';

      document.cookie =
        `${this.config.cookieName}=${encodeURIComponent(localeKey)}` +
        `; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
    }

    getCookie(name) {
      const prefix = `${name}=`;

      const row = document.cookie
        .split(';')
        .map((part) => part.trim())
        .find((part) => part.startsWith(prefix));

      if (!row) return null;

      try {
        return decodeURIComponent(row.slice(prefix.length));
      } catch {
        return null;
      }
    }
  }

  customElements.define('market-switch', MarketSwitch);
}